import { Control, Controller, UseFormWatch } from 'react-hook-form';
import { ContactSyncModel } from '../../../../../types';
import AvatarChip from '../../../../shared/avatar-chip';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface StepSixProps {
  control: Control<Partial<ContactSyncModel>>;
  watch: UseFormWatch<Partial<ContactSyncModel>>;
}

export default function StepSix({ control, watch }: StepSixProps) {
  const sourceUser = watch('sourceUser');
  const allSource = watch('allSource');
  const targetType = watch('targetType')!;
  const targets = watch('targets') || [];

  return (
    <Box
      sx={{
        viewTransitionName: 'steps',
      }}
    >
      <Stack gap={2} sx={{ pl: 4 }}>
        <Controller
          name="runImmediately"
          control={control}
          render={({ field: props }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...props}
                  checked={props.value}
                  onChange={(e) => props.onChange(e.target.checked)}
                />
              }
              label="Run contact sync immediately after saving"
            />
          )}
        />
        <Grid container spacing={3}>
          <Grid size={{ xs: 4 }}>
            <Stack>
              <Typography
                sx={{
                  fontWeight: 600,
                }}
              >
                Sync Name:
              </Typography>
              <Typography
                noWrap
                sx={{
                  maxWidth: 200,
                  textOverflow: 'ellipsis',
                }}
              >
                {watch('name')}
              </Typography>
            </Stack>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Stack>
              <Typography
                sx={{
                  fontWeight: 600,
                }}
              >
                Sync Type:
              </Typography>
              <Typography
                noWrap
                sx={{
                  maxWidth: 200,
                  textOverflow: 'ellipsis',
                }}
              >
                {watch('syncType') === 'contact'
                  ? 'Personal Contacts'
                  : watch('syncType') === 'directory'
                  ? 'Directory Contacts'
                  : 'Bi-Directional'}
              </Typography>
            </Stack>
          </Grid>
          {watch('description')!.length > 0 ? (
            <Grid size={{ xs: 4 }}>
              <Stack>
                <Typography
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  Description:
                </Typography>
                <Typography
                  noWrap
                  sx={{
                    maxWidth: 200,
                    textOverflow: 'ellipsis',
                  }}
                >
                  {watch('description')}
                </Typography>
              </Stack>
            </Grid>
          ) : null}
          {watch('syncType') === 'contact' ? (
            <Grid size={{ xs: 4 }}>
              <Stack>
                <Typography
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  Sync User:
                </Typography>
                <Typography
                  sx={{
                    maxWidth: 250,
                    textOverflow: 'ellipsis',
                  }}
                  noWrap
                >
                  <AvatarChip
                    email={sourceUser || ''}
                    type="User"
                    size="small"
                  />
                </Typography>
              </Stack>
            </Grid>
          ) : null}
          {watch('syncType') === 'contact' ? (
            <Grid size={{ xs: 4 }}>
              <Stack>
                <Typography
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  Source Label
                </Typography>
                <Typography
                  noWrap
                  sx={{
                    maxWidth: 200,
                    textOverflow: 'ellipsis',
                  }}
                >
                  {watch('groupTitle')}
                </Typography>
              </Stack>
            </Grid>
          ) : null}
          <Grid size={{ xs: 4 }}>
            <Stack>
              <Typography
                sx={{
                  fontWeight: 600,
                }}
              >
                Target Label:
              </Typography>
              <Typography
                noWrap
                sx={{
                  maxWidth: 200,
                  textOverflow: 'ellipsis',
                }}
              >
                {watch('targetGroupName')!.length > 0
                  ? watch('targetGroupName')
                  : watch('syncType') === 'directory'
                  ? 'Directory Contacts'
                  : watch('groupTitle')}
              </Typography>
            </Stack>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Stack>
              <Typography
                sx={{
                  fontWeight: 600,
                }}
              >
                Target Type
              </Typography>
              <Typography
                noWrap
                sx={{
                  maxWidth: 200,
                  textOverflow: 'ellipsis',
                }}
              >
                {targetType}
              </Typography>
            </Stack>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Stack>
              <Typography
                sx={{
                  fontWeight: 600,
                }}
              >
                Targets:
              </Typography>
              <Stack
                sx={{ width: 270, maxHeight: 100, overflow: 'auto' }}
                gap={0.5}
              >
                {allSource ? (
                  <Typography>All</Typography>
                ) : (
                  targets.map((target) => {
                    return (
                      <AvatarChip
                        key={target}
                        email={target}
                        type={
                          targetType as 'User' | 'Group' | 'OrgUnit' | 'Domain'
                        }
                        size="small"
                      />
                    );
                  })
                )}
              </Stack>
            </Stack>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Stack>
              <Typography
                sx={{
                  fontWeight: 600,
                }}
              >
                Scheduled:
              </Typography>
              <Typography
                noWrap
                sx={{
                  maxWidth: 200,
                  textOverflow: 'ellipsis',
                }}
              >
                {watch('scheduled') ? 'Yes' : 'No'}
              </Typography>
            </Stack>
          </Grid>
          {watch('scheduled') ? (
            <Grid size={{ xs: 4 }}>
              <Stack flexDirection={'row'} gap={2}>
                <Typography
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  Times:
                </Typography>
                <Box sx={{ width: 80, maxHeight: 70, overflow: 'auto' }}>
                  {watch('times')!.map((time) => {
                    return (
                      <Typography
                        key={time}
                        noWrap
                        sx={{
                          maxWidth: 80,
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {parseInt(time) === 0
                          ? '12 AM'
                          : parseInt(time) < 12
                          ? `${time} AM`
                          : parseInt(time) === 12
                          ? `${time} PM`
                          : `${parseInt(time) - 12} PM`}
                      </Typography>
                    );
                  })}
                </Box>
              </Stack>
            </Grid>
          ) : null}
          {watch('scheduled') ? (
            <Grid size={{ xs: 4 }}>
              <Stack flexDirection={'row'} gap={2}>
                <Typography
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  Dates:
                </Typography>
                <Box sx={{ width: 200, maxHeight: 100, overflow: 'auto' }}>
                  {watch('dates')!.map((date) => {
                    return (
                      <Typography
                        key={date}
                        noWrap
                        sx={{
                          maxWidth: 200,
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {date}
                      </Typography>
                    );
                  })}
                </Box>
              </Stack>
            </Grid>
          ) : null}
          {watch('scheduled') &&
          !!watch('scheduleEndDate') &&
          watch('scheduleEndDate') !== 0 ? (
            <Grid size={{ xs: 4 }}>
              <Stack>
                <Typography
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  Expiration Date:
                </Typography>
                <Typography
                  noWrap
                  sx={{
                    maxWidth: 200,
                    textOverflow: 'ellipsis',
                  }}
                >
                  {new Date(watch('scheduleEndDate')!).toLocaleDateString()}
                </Typography>
              </Stack>
            </Grid>
          ) : null}
        </Grid>
      </Stack>
    </Box>
  );
}
